import { httpClient } from '@shared/infrastructure/http/http-client.js';
import type {
  TranscriptionError,
  TranscriptionStatus,
  TranscriptSegment,
  TranscriptWord,
  VideoTranscription,
} from '../model.js';

/**
 * Virtual Profiler's document without its `_id`, passed through by the back-office as it is.
 *
 * Seconds, with the audio's offset already applied: the same bundle coherence reads. When the video
 * has no document the answer still comes, with `status: "none"` and empty lists.
 */
interface VideoTranscriptionPayload {
  source_type?: string | null;
  source_id?: number | string | null;
  status?: string | null;
  attempts?: number | null;
  requested_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  provider?: string | null;
  model?: string | null;
  language?: string | null;
  segments?: Array<{
    id?: string | null;
    startTime: number;
    endTime: number;
    text: string;
    speaker?: string | null;
    confidence?: number | null;
  }> | null;
  words?: Array<{
    word_index?: number | null;
    word: string;
    start_time: number;
    end_time: number;
  }> | null;
  error?: { type?: string | null; message?: string | null } | null;
}

const STATUSES: ReadonlySet<string> = new Set<TranscriptionStatus>([
  'none',
  'pending',
  'running',
  'done',
  'failed',
]);

/**
 * A status this build does not know reads as `none`: the panel then offers to ask for one, and
 * upstream answers what it has instead of the screen inventing a state.
 */
function toStatus(value: string | null | undefined): TranscriptionStatus {
  return typeof value === 'string' && STATUSES.has(value) ? (value as TranscriptionStatus) : 'none';
}

function toDate(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

function toMs(seconds: number): number {
  return Math.round(seconds * 1_000);
}

function toSegment(
  segment: NonNullable<VideoTranscriptionPayload['segments']>[number],
): TranscriptSegment {
  return {
    startMs: toMs(segment.startTime),
    endMs: toMs(segment.endTime),
    // Upstream's own word for a speaker it could not tell, as the analysis transcription has it.
    speaker: segment.speaker ?? 'unknown',
    text: segment.text,
  };
}

/** Words carry no speaker: coherence assigns one later, by the segment they fall in. */
function toWord(word: NonNullable<VideoTranscriptionPayload['words']>[number]): TranscriptWord {
  return {
    word: word.word,
    startMs: toMs(word.start_time),
    endMs: toMs(word.end_time),
    speaker: 'unknown',
  };
}

function toError(payload: VideoTranscriptionPayload['error']): TranscriptionError | null {
  if (!payload) return null;

  return { type: payload.type ?? 'transcription_failed', message: payload.message ?? '' };
}

function toTranscription(
  payload: VideoTranscriptionPayload | null | undefined,
  videoId: number,
): VideoTranscription {
  const doc = payload ?? {};

  return {
    // Upstream passes ids through, and a source id has travelled as text before.
    videoId:
      doc.source_id === null || doc.source_id === undefined ? videoId : Number(doc.source_id),
    status: toStatus(doc.status),
    attempts: typeof doc.attempts === 'number' ? doc.attempts : 0,
    requestedAt: toDate(doc.requested_at),
    startedAt: toDate(doc.started_at),
    finishedAt: toDate(doc.finished_at),
    provider: doc.provider ?? null,
    model: doc.model ?? null,
    language: doc.language ?? null,
    segments: (doc.segments ?? []).map(toSegment),
    words: (doc.words ?? []).map(toWord),
    error: toError(doc.error),
  };
}

function base(videoId: number): string {
  return `/virtual-profiler/videos/${videoId}/transcription`;
}

/**
 * Boundary with a video's own transcription (product owner, 2026-09-28).
 *
 * Per video, never per run: every uploaded video is transcribed once by a job of its own, so it can
 * be read — and labelled against — before any analysis, and coherence reads it instead of
 * transcribing again. Asking again is only for a video that has none or whose job failed; upstream
 * answers 409 (`VP_TRANSCRIPTION_BUSY`) while one is on its way or already done.
 */
export const videoTranscriptionsApi = {
  get: async (videoId: number): Promise<VideoTranscription> =>
    toTranscription(await httpClient.get<VideoTranscriptionPayload>(base(videoId)), videoId),

  /** «Transcribir» or «Reintentar»: answers the document as it is once the job was asked for. */
  retry: async (videoId: number): Promise<VideoTranscription> =>
    toTranscription(
      await httpClient.post<VideoTranscriptionPayload>(`${base(videoId)}/retry`),
      videoId,
    ),
};
